import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import CTAStrip from "@/components/CTAStrip";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";

const STEPS = [
  {
    n: "01",
    t: "Discovery",
    d: "We map your appetite, conversion economics and territory. We learn what binds — and what doesn't.",
    out: [
      "Appetite & filter sheet",
      "Target cost-per-bind",
      "State & territory map",
    ],
  },
  {
    n: "02",
    t: "Funnel build",
    d: "Our creative, dev and compliance teams build the campaign — landing pages, ad creative, qualification flow, consent capture.",
    out: [
      "Vertical-specific funnels",
      "TCPA-compliant consent",
      "Multi-channel creative",
    ],
  },
  {
    n: "03",
    t: "Launch",
    d: "Soft launch into a contained market. Real-time delivery to your CRM or call center. Measure, iterate, scale.",
    out: [
      "Real-time POST or call routing",
      "Sub-60-second latency",
      "Closed-loop reporting",
    ],
  },
  {
    n: "04",
    t: "Optimization",
    d: "Weekly performance reviews. Budget moves to the campaigns binding hardest. Underperformers get rebuilt, not excused.",
    out: [
      "Weekly portfolio review",
      "Filter refinement",
      "Creative refresh",
    ],
  },
];

const PRODUCTS = [
  { t: "Exclusive data", d: "Sold to one partner. Highest contact rates." },
  { t: "Shared data", d: "Sold 2–3 ways. Best CPL economics." },
  { t: "Live calls", d: "Inbound from intent-driven creative." },
  { t: "Hot transfers", d: "Pre-qualified by our agents." },
  { t: "Appointment-set", d: "Booked on your calendar." },
  { t: "Aged data", d: "Re-marketable records at discount pricing." },
];

const FAQ = [
  {
    q: "What does a typical engagement look like?",
    a: "Most partners start with a contained test (one state, one product type, two-week ramp). Once we hit your KPI we expand by geography, then by product type (data → live → transfer).",
  },
  {
    q: "How are leads priced?",
    a: "By product type and vertical. Data is priced per record, calls and transfers by billable duration, appointment-set by show. All pricing is performance-tiered with quality SLAs.",
  },
  {
    q: "Is the consent TCPA compliant?",
    a: "Yes. Every lead is captured with explicit, scoped consent, retained with timestamp, IP, user agent and form snapshot. We provide audit packages on demand.",
  },
  {
    q: "Can you match our underwriting appetite?",
    a: "That's the entire point. We work with your appetite document and build qualification flows that pre-screen out risks you can't write.",
  },
  {
    q: "Do you work outside California?",
    a: "We deliver leads in all 50 states. Citirex itself is headquartered in Los Angeles, but our partners operate nationally.",
  },
];

export default function HowItWorks() {
  return (
    <main data-testid="how-it-works-page">
      <PageHead
        title="How it works"
        description="From appetite to bind rate in four moves — Citirex's portfolio-management approach to performance lead generation."
        path="/how-it-works"
      />
      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">How it works</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[110px]">
              From appetite to{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                bind rate
              </span>
              , in four moves.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-citirex-sub md:text-xl">
              The way most agencies run lead-gen is: take a brief, push some
              traffic, send invoices. Ours is closer to portfolio
              management.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="bg-citirex-bg p-8 md:p-12"
                data-testid={`how-it-works-step-${s.n}`}
              >
                <p className="font-editorial text-6xl italic text-citirex-accent">
                  {s.n}
                </p>
                <h3 className="mt-6 font-display text-3xl font-medium md:text-4xl">
                  {s.t}
                </h3>
                <p className="mt-4 font-body text-base text-citirex-sub md:text-lg">
                  {s.d}
                </p>
                <ul className="mt-8 space-y-2 border-t border-citirex-border pt-6">
                  {s.out.map((o) => (
                    <li
                      key={o}
                      className="flex items-center gap-3 font-body text-sm text-citirex-ink"
                    >
                      <span className="h-1.5 w-1.5 bg-citirex-accent" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-citirex-border bg-citirex-ink text-citirex-inv">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <p className="overline">Product menu</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.05] md:text-6xl">
            Buy the format that{" "}
            <span className="font-editorial italic">your team can close</span>.
          </h2>
          <div className="mt-16 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.map((p) => (
              <div key={p.t} className="bg-citirex-ink p-8">
                <h4 className="font-display text-2xl font-medium">{p.t}</h4>
                <p className="mt-3 font-body text-sm text-citirex-inv/70">
                  {p.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="overline">Frequently asked</p>
              <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Questions our{" "}
                <span className="font-editorial italic">best partners</span>{" "}
                ask first.
              </h2>
            </div>
            <div className="md:col-span-7">
              <Accordion
                type="single"
                collapsible
                className="border-t border-citirex-border"
                data-testid="how-it-works-faq"
              >
                {FAQ.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`item-${i}`}
                    className="border-b border-citirex-border"
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="py-6 text-left font-display text-xl font-medium tracking-tight text-citirex-ink hover:text-citirex-accent hover:no-underline md:text-2xl"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 font-body text-base text-citirex-sub md:text-lg">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      <CTAStrip
        eyebrow="Plug in"
        title="Pilot a vertical with us in two weeks."
        buttonText="Start a pilot"
      />
    </main>
  );
}
